import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import './MultiSelectBox.scss';
import CustomOption from './CustomOption/CustomOption';
import { Z_INDEXES } from '@Constants/constants';

class MultiSelectBox extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            iOS: false,
            hasSubmitted: false,
        };
        this.containerRef = React.createRef();
        this.inputRef = null;
    }

    componentDidMount() {
        const iOS = this.props.forceDesktopLayout ? false : navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
        this.setState({ iOS });

        this.attachFormValidationObserver();

        setTimeout(() => {
            if (this.inputRef) {
                this.inputRef.addEventListener('paste', this.handlePaste);
            }
        }, 100);
    }

    componentWillUnmount() {
        this.removeFormValidationObserver();

        if (this.inputRef) {
            this.inputRef.removeEventListener('paste', this.handlePaste);
        }
    }

    handlePaste = (e) => {
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');

        const abbreviations = pastedText
            .split(/[\s,;\n\t]+/)
            .map((x) => x.trim().toLowerCase())
            .filter(Boolean);

        const options = this.props.options || [];

        //options that match the abbreviations
        const matched = options
            .filter((opt) => {
                const valueField = (opt[this.props.valueField] || '').toLowerCase();
                return abbreviations.some((abbr) => {
                    const match = valueField.includes(abbr);
                    return match;
                });
            })
            .map((opt) => opt[this.props.idField].toString());

        if (matched.length > 0) {
            const current = (this.props.selectedValues || '').split(',').filter(Boolean);
            const updated = Array.from(new Set([...current, ...matched]));

            this.props.onChange(updated.join(','), this.props.name);
            e.preventDefault();
        } else {
            console.log('❌ No matches found.');
        }
    };

    /** 🔹 Observe the form for class changes */
    attachFormValidationObserver = () => {
        const form = this.containerRef.current?.closest('form');
        if (form) {
            // Use MutationObserver to detect when `was-validated` is added
            this.observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.target.classList.contains('was-validated')) {
                        this.setState({ formValidated: true }); // ✅ Force update immediately
                    }
                });
            });

            // Observe class attribute changes on the form
            this.observer.observe(form, { attributes: true, attributeFilter: ['class'] });
        }
    };

    /** 🔹 Remove observer when component unmounts */
    removeFormValidationObserver = () => {
        if (this.observer) {
            this.observer.disconnect();
        }
    };

    handleReactSelectChange = (selectedOption) => {
        if (this.props.isMulti) {
            const selectedValue = Array.isArray(selectedOption) ? selectedOption.map((option) => option['value']).join(',') : '';
            this.props.onChange(selectedValue, this.props.name);
        } else {
            const selectedValue = selectedOption && selectedOption['value'] !== undefined ? selectedOption['value'] : 0;
            this.props.onChange(selectedValue, this.props.name);
        }
    };

    handleDomChange = (e) => {
        const selected = [...e.target.options].filter((o) => o.selected).map((o) => o.value);
        this.props.onChange(selected.join(), this.props.name);
    };

    handleKeyDown = (e) => {
        if (this.props.onInputKeyDown) {
            this.props.onInputKeyDown(e);
        }
    };

    onInputChange = (value) => {
        if (this.props.onInputChange) {
            this.props.onInputChange(value);
        }
    };

    render() {
        let multiSelect = null;
        const className = this.props.className || '';

        // **Show error only after submission**
        const showError = this.state.formValidated && this.props.isRequired && this.props.selectedValues.trim().length === 0;

        const invalidClass = showError ? 'is-invalid' : '';

        // let selectedOption = null;

        // if (shouldShowIcons) {
        //     selectedOption = this.props.options.find((option) => option[this.props.idField].toString() === this.props.selectedValues);
        // }

        if (this.state.iOS) {
            const values = this.props.isMulti ? this.props.selectedValues.split(',') : this.props.selectedValues;
            const mobilePlaceholder = this.props.placeholder || 'Select...';

            multiSelect = (
                <select
                    className='pacer-multi-select'
                    multiple={this.props.isMulti}
                    id={this.props.name}
                    name={this.props.name}
                    onChange={this.handleDomChange}
                    value={values}
                    required={this.props.isRequired}>
                    <option style={{ zIndex: 1000 }} disabled value=''>
                        {mobilePlaceholder}
                    </option>
                    {this.props.options.map((option) => (
                        <option style={{ zIndex: 1000 }} key={option[this.props.idField]} value={option[this.props.idField]}>
                            {option[this.props.valueField]}
                        </option>
                    ))}
                </select>
            );
        } else {
            const options = (this.props.options || []).map((option, i) => {
                const styleName = `${this.props.name}_optionStyle_${i}`;
                if (option.styles) {
                    const styleSheet = document.createElement('style');
                    styleSheet.innerHTML = `
                        .${styleName} {
                            background-color: ${option.styles.backgroundColor} !important;
                            color: ${option.styles.color} !important;
                        }
                    `;
                    document.body.appendChild(styleSheet);
                }
                return {
                    value: option[this.props.idField],
                    label: option[this.props.valueField],
                    clearableValue: true,
                    className: styleName,
                    styles: option.styles || {}, // Include styles here
                    itemIcon: option.itemIcon,
                    isSeparator: option.isSeparator,
                };
            });

            const closeOnSelect = !this.props.isMulti;
            const clearable = this.props.isMulti;
            const searchable = this.props.isSearchable !== undefined ? this.props.isSearchable : true;

            const placeholder = this.props.placeholder || (this.props.isMulti ? 'Select all that apply...' : undefined);

            // Custom styles for react-select
            const customStyles = {
                control: (base, state) => ({
                    ...base,
                    borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
                    '&:hover': { borderColor: '#ced4da' },
                }),
                placeholder: (base) => ({
                    ...base,
                    color: '#6c757d',
                }),
                option: (base, { data, isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                        ? data.styles?.backgroundColor || '#007bff'
                        : isFocused
                            ? '#f8f9fa'
                            : data.styles?.backgroundColor || 'white',
                    color: isSelected ? data.styles?.color || 'white' : data.styles?.color || '#212529',
                    '&:active': {
                        backgroundColor: data.styles?.backgroundColor || '#0056b3',
                    },
                }),
                singleValue: (base, { data }) => ({
                    ...base,
                    color: data.styles?.color || base.color,
                }),
                multiValue: (base, { data }) => ({
                    ...base,
                    backgroundColor: data.styles?.backgroundColor || '#e2e3e5', // Use custom or default background
                    color: data.styles?.color || '#212529', // Use custom or default text color
                }),
                multiValueLabel: (base, { data }) => ({
                    ...base,
                    color: data.styles?.color || '#212529', // Keep text color consistent
                }),
                multiValueRemove: (base, { data }) => ({
                    ...base,
                    color: data.styles?.color || '#212529',
                    '&:hover': {
                        backgroundColor: data.styles?.backgroundColor || '#d6d8db', // Match hover with background
                        color: data.styles?.color || '#212529',
                    },
                }),
                indicatorSeparator: () => ({
                    display: 'none',
                }),
                menuPortal: (base) => ({ ...base, zIndex: Z_INDEXES.multiSelectBox }),

            };

            // Normalize selectedValues for react-select
            const normalizedValues = this.props.isMulti
                ? (this.props.selectedValues ?? '')
                    .split(',')
                    .map((id) => {
                        if (!id) return null;

                        const option = options.find((option) => option?.value !== undefined && option.value?.toString() === id.toString());

                        return option || null;
                    })
                    .filter(Boolean)
                : options.find(
                    (option) => option?.value !== undefined && option.value?.toString() === (this.props.selectedValues ?? '').toString()
                ) || null;

            // Map options for react-select
            const selectOptions = options.map((option) => ({
                value: option['value'],
                label: option['label'],
                icon: option['itemIcon'],
                styles: option['styles'],
                ...option,
            }));

            multiSelect = (
                <Select
                    menuPortalTarget={document.body}
                    className={`pacer-multi-select ${invalidClass}`}
                    name={this.props.name}
                    value={normalizedValues}
                    onChange={this.handleReactSelectChange}
                    closeMenuOnSelect={closeOnSelect}
                    isSearchable={searchable}
                    isClearable={clearable}
                    isMulti={this.props.isMulti}
                    options={selectOptions}
                    styles={customStyles}
                    required={this.props.isRequired}
                    onInputKeyDown={this.handleKeyDown}
                    onInputChange={this.onInputChange}
                    defaultValue={this.props.defaultValue}
                    placeholder={placeholder}
                    isDisabled={this.props.isDisabled}
                    autoFocus={this.props.isFocused}
                    components={{ Option: CustomOption }}
                    onKeyDown={this.handleKeyDown}
                    ref={(ref) => {
                        if (ref && !this.inputRef) {
                            // Delay ensures DOM is rendered
                            setTimeout(() => {
                                const inputEl = ref?.inputRef;
                                if (inputEl && !this.inputRef) {
                                    this.inputRef = inputEl;
                                    this.inputRef.addEventListener('paste', this.handlePaste);
                                }
                            }, 0);
                        }
                    }}
                />
            );
        }

        return (
            <div className={`${className || 'mb-3'}`} ref={this.containerRef}>
                {/* {shouldShowIcons && selectedOption ? (
                    <div className="option-icon">
                        <Icon name={selectedOption.icon} />
                    </div>
                ) : null} */}
                <span className='has-float-label'>
                    {multiSelect}
                    <label htmlFor={this.props.name}>{this.props.label}</label>
                    {showError && <div className='invalid-feedback'>{this.props.errorMessage}</div>}
                </span>
            </div>
        );
    }
}

MultiSelectBox.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    idField: PropTypes.string.isRequired,
    valueField: PropTypes.string.isRequired,
    className: PropTypes.string,
    options: PropTypes.array.isRequired,
    selectedValues: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onInputKeyDown: PropTypes.func,
    onInputChange: PropTypes.func,
    isRequired: PropTypes.bool,
    errorMessage: PropTypes.string,
    isMulti: PropTypes.bool.isRequired,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    isDisabled: PropTypes.bool,
    isSearchable: PropTypes.bool,
    isFocused: PropTypes.bool,
    optionComponent: PropTypes.func,
    forceDesktopLayout: PropTypes.bool,
};

export default MultiSelectBox;
